/**
 * Qual modelo mostrar no hero de uma run.
 *
 * O hero da página do agente anuncia uma run específica, então o modelo tem de
 * ser o **daquela** run — não o configurado hoje. O backend grava o id em
 * `metrics.model` e o rótulo legível em `metrics.model_label`.
 *
 * A cadeia de fallback existe porque runs antigas não têm o rótulo, e algumas
 * não têm nem o id: nesses casos o modelo configurado hoje é a melhor
 * aproximação disponível, e é melhor que o literal cravado que estava aqui
 * antes ("Claude 3.5 Sonnet", duas gerações atrás do que a plataforma roda).
 */

export interface RunModelMetrics {
  /** Rótulo derivado pelo backend a partir de `model`. */
  model_label?: string;
  /** Id cru do Bedrock gravado pelo executor. */
  model?: string;
}

/**
 * Rótulo do modelo de uma run, ou `null` quando nada é conhecido.
 *
 * O id cru vem **antes** do modelo configurado: `anthropic.algo-novo` é feio,
 * mas é a verdade sobre aquela run; o modelo de hoje seria bonito e errado.
 */
/** Um modelo que produziu custo, como o `GET /costs` devolve. */
export interface CostModel {
  id: string;
  label: string;
}

/** O primeiro texto não vazio de uma entrada de modelo. */
function displayOf(model: CostModel): string {
  return model.label?.trim() || model.id?.trim() || "";
}

/**
 * Texto da linha "Model" de uma vitrine de custos, ou `null` quando não há
 * medição.
 *
 * É uma **lista** porque o custo de um projeto pode ter sido produzido por mais
 * de um modelo: runs de fevereiro num, de março noutro, que é o caso normal de
 * uma migração de modelo. Antes a api publicava um escalar tirado da própria
 * configuração — e ela nem é o serviço que executa as runs.
 */
export function costModelsLabel(models: CostModel[] | null | undefined): string | null {
  const labels = (models ?? []).map(displayOf).filter(Boolean);
  return labels.length > 0 ? labels.join(" · ") : null;
}

/**
 * O único modelo medido do projeto, ou `null` quando há zero ou mais de um.
 *
 * Serve de referência para uma run que não gravou o modelo dela. Com dois
 * modelos no projeto não existe resposta honesta — escolher um seria repetir o
 * palpite que esta mudança veio tirar.
 */
export function soleModelLabel(models: CostModel[] | null | undefined): string | null {
  return models?.length === 1 ? displayOf(models[0]) || null : null;
}

export function runModelLabel(
  metrics: RunModelMetrics | null | undefined,
  configuredLabel?: string | null,
): string | null {
  // `bedrock_model_label("")` devolve `""`, então string vazia significa "não
  // sei", não "rótulo vazio" — sem o trim o hero renderizaria "Model" sozinho.
  const candidates = [metrics?.model_label, metrics?.model, configuredLabel];
  for (const candidate of candidates) {
    const value = candidate?.trim();
    if (value) return value;
  }
  return null;
}
